import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Toggle } from "@/components/ui/toggle";
import { useTranslation } from "@/hooks/useTranslation";
import { toHumanReadableAmount } from "@/lib/string";
import { cn } from "@/lib/utils";
import type { ForecastData } from "@/lib/parse-forecast";
import { CompanyDTO } from "@shaarp/common";
import {
  BarChart3Icon,
  LineChartIcon,
  Loader2Icon,
  TableIcon,
} from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from "recharts";

type ChartType = "bar" | "line";

type MetricKey = "revenue" | "netResult" | "ebitda" | "grossMargin";

type ForecastMetricKey =
  | "forecastRevenue"
  | "forecastNetResult"
  | "forecastEbitda"
  | "forecastGrossMargin";

const METRIC_COLORS: Record<MetricKey, string> = {
  revenue: "hsl(217, 91%, 60%)",
  netResult: "hsl(160, 60%, 45%)",
  ebitda: "hsl(35, 92%, 55%)",
  grossMargin: "hsl(280, 65%, 55%)",
};

const FORECAST_KEY_MAP: Record<MetricKey, ForecastMetricKey> = {
  revenue: "forecastRevenue",
  netResult: "forecastNetResult",
  ebitda: "forecastEbitda",
  grossMargin: "forecastGrossMargin",
};

type CardFinancialsHistoryProps = {
  company: CompanyDTO;
  forecastData?: ForecastData | null;
  isForecastLoading?: boolean;
};

export default function CardFinancialsHistory({
  company,
  forecastData,
  isForecastLoading = false,
}: CardFinancialsHistoryProps) {
  const { t } = useTranslation();
  const finances = company.business.finances;

  if (!finances || finances.length === 0) return null;

  const sorted = [...finances].sort((a, b) => b.year - a.year);
  const chronological = [...finances].sort((a, b) => a.year - b.year);

  const availableMetrics = useMemo((): MetricKey[] => {
    const has = (key: keyof (typeof finances)[0]): boolean =>
      finances.some((f) => f[key] != null);

    const metrics: MetricKey[] = [];
    if (has("sales")) metrics.push("revenue");
    if (has("result")) metrics.push("netResult");
    if (has("ebitda")) metrics.push("ebitda");
    if (has("grossMargin")) metrics.push("grossMargin");
    return metrics;
  }, [finances]);

  const [chartType, setChartType] = useState<ChartType>("line");
  const [activeMetrics, setActiveMetrics] = useState<MetricKey[]>(
    () => availableMetrics,
  );
  const [showForecast, setShowForecast] = useState(true);


  const baseChartData = useMemo(
    () =>
      chronological.map((f) => ({
        year: f.year.toString(),
        revenue: f.sales ?? 0,
        netResult: f.result ?? 0,
        ebitda: f.ebitda ?? 0,
        grossMargin: f.grossMargin ?? 0,
        forecastRevenue: undefined as number | undefined,
        forecastNetResult: undefined as number | undefined,
        forecastEbitda: undefined as number | undefined,
        forecastGrossMargin: undefined as number | undefined,
      })),
    [chronological],
  );

  const hasForecast = forecastData != null && showForecast;

  const chartData = useMemo(() => {
    if (!forecastData || !showForecast) return baseChartData;

    const lastRealYear = chronological[chronological.length - 1]?.year;
    if (forecastData.year === lastRealYear?.toString()) return baseChartData;

    // Bridge: set forecast values on the last real data point so the dashed line connects
    const data = baseChartData.map((d, i) => {
      if (i === baseChartData.length - 1) {
        return {
          ...d,
          forecastRevenue:
            forecastData.revenue != null ? d.revenue : undefined,
          forecastNetResult:
            forecastData.netResult != null ? d.netResult : undefined,
          forecastEbitda:
            forecastData.ebitda != null ? d.ebitda : undefined,
          forecastGrossMargin:
            forecastData.grossMargin != null ? d.grossMargin : undefined,
        };
      }
      return d;
    });

    // Forecast point — real metrics are undefined so solid lines stop at the last real point
    data.push({
      year: `${forecastData.year} (estimation Shaarp)`,
      revenue: undefined as unknown as number,
      netResult: undefined as unknown as number,
      ebitda: undefined as unknown as number,
      grossMargin: undefined as unknown as number,
      forecastRevenue: forecastData.revenue,
      forecastNetResult: forecastData.netResult,
      forecastEbitda: forecastData.ebitda,
      forecastGrossMargin: forecastData.grossMargin,
    });

    return data;
  }, [baseChartData, forecastData, showForecast, chronological]);

  const metricLabels: Record<MetricKey, string> = {
    revenue: t("company.insights.financials.revenue"),
    netResult: t("company.insights.financials.netResult"),
    ebitda: "EBITDA",
    grossMargin: t("company.insights.financials.grossMargin"),
  };

  const chartConfig: ChartConfig = Object.fromEntries(
    availableMetrics.map((key) => [
      key,
      { label: metricLabels[key], color: METRIC_COLORS[key] },
    ]),
  );

  const toggleMetric = (metric: MetricKey): void => {
    setActiveMetrics((prev) => {
      if (prev.includes(metric)) {
        if (prev.length <= 1) return prev;
        return prev.filter((m) => m !== metric);
      }
      return [...prev, metric];
    });
  };

  const formatYAxis = (value: number): string => {
    if (Math.abs(value) >= 1_000_000)
      return `${(value / 1_000_000).toFixed(1)}M`;
    if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
    return value.toString();
  };

  const sharedAxisProps = {
    xAxis: {
      dataKey: "year" as const,
      tickLine: false,
      axisLine: false,
      tickMargin: 8,
    },
    yAxis: {
      tickLine: false,
      axisLine: false,
      tickMargin: 8,
      tickFormatter: formatYAxis,
    },
  };

  const tooltipFormatter = (
    value: unknown,
    name: string,
    item: { payload?: Record<string, unknown> },
  ) => {
    const isForecast = name.startsWith("forecast");
    const metricKey = isForecast
      ? ((name.replace("forecast", "").charAt(0).toLowerCase() +
          name.replace("forecast", "").slice(1)) as MetricKey)
      : (name as MetricKey);

    // On real data points (bridge), hide forecast entries to avoid duplicates
    const isForecastYear = String(item.payload?.year ?? "").includes(
      "(estimation Shaarp)",
    );
    if (isForecast && !isForecastYear) return null;

    return (
      <>
        <div
          className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
          style={
            isForecast
              ? { background: "linear-gradient(90deg, #E2CBFF, #393BB2)" }
              : { backgroundColor: METRIC_COLORS[metricKey] }
          }
        />
        <div className="flex flex-1 justify-between gap-4 leading-none items-center">
          <span className="text-muted-foreground">
            {metricLabels[metricKey] ?? name}
          </span>
          <span className="font-mono font-medium tabular-nums text-foreground">
            {toHumanReadableAmount(value as number)}
          </span>
        </div>
      </>
    );
  };

  const forecastGradientDef = (
    <defs>
      <linearGradient id="forecastGradient" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#E2CBFF" />
        <stop offset="100%" stopColor="#393BB2" />
      </linearGradient>
    </defs>
  );

  return (
    <Card>
      <CardHeader className="pb-3 sm:pb-4">
        <CardTitle className="flex items-center gap-3 text-lg">
          <div className="flex items-center justify-center w-10 h-10 rounded-full shrink-0 bg-muted">
            <TableIcon className="w-5 h-5" />
          </div>
          {t("company.financials.history.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="chart">
          <TabsList className="mb-4">
            <TabsTrigger value="table" className="gap-1.5">
              <TableIcon className="w-3.5 h-3.5" />
              {t("company.financials.history.viewTable")}
            </TabsTrigger>
            <TabsTrigger value="chart" className="gap-1.5">
              <BarChart3Icon className="w-3.5 h-3.5" />
              {t("company.financials.history.viewChart")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="py-2 pr-4 font-medium text-left text-muted-foreground">
                      {t("company.financials.history.year")}
                    </th>
                    <th className="py-2 px-4 font-medium text-right text-muted-foreground">
                      {t("company.insights.financials.revenue")}
                    </th>
                    <th className="py-2 px-4 font-medium text-right text-muted-foreground">
                      {t("company.insights.financials.netResult")}
                    </th>
                    <th className="py-2 px-4 font-medium text-right text-muted-foreground">
                      EBITDA
                    </th>
                    <th className="py-2 px-4 font-medium text-right text-muted-foreground">
                      {t("company.insights.financials.grossMargin")}
                    </th>
                    <th className="py-2 pl-4 font-medium text-right text-muted-foreground">
                      {t("company.insights.financials.employees")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((finance) => (
                    <tr
                      key={finance.year}
                      className="border-b last:border-b-0"
                    >
                      <td className="py-2.5 pr-4 font-semibold">
                        {finance.year}
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        {finance.sales != null
                          ? toHumanReadableAmount(finance.sales)
                          : "—"}
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        {finance.result != null
                          ? toHumanReadableAmount(finance.result)
                          : "—"}
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        {finance.ebitda != null
                          ? toHumanReadableAmount(finance.ebitda)
                          : "—"}
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        {finance.grossMargin != null
                          ? toHumanReadableAmount(finance.grossMargin)
                          : "—"}
                      </td>
                      <td className="py-2.5 pl-4 text-right">
                        {finance.headcount ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="chart">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="flex items-center rounded-lg border bg-muted/40 p-0.5">
                <button
                  type="button"
                  onClick={() => setChartType("bar")}
                  className={cn(
                    "inline-flex items-center justify-center rounded-md p-1.5 transition-colors",
                    chartType === "bar"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <BarChart3Icon className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setChartType("line")}
                  className={cn(
                    "inline-flex items-center justify-center rounded-md p-1.5 transition-colors",
                    chartType === "line"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <LineChartIcon className="w-4 h-4" />
                </button>
              </div>

              <div className="h-5 w-px bg-border" />

              <div className="flex flex-wrap items-center gap-1.5">
                {availableMetrics.map((metric) => (
                  <Toggle
                    key={metric}
                    size="sm"
                    variant="outline"
                    pressed={activeMetrics.includes(metric)}
                    onPressedChange={() => toggleMetric(metric)}
                    className="gap-1.5 text-xs"
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: METRIC_COLORS[metric] }}
                    />
                    {metricLabels[metric]}
                  </Toggle>
                ))}
              </div>

              {(forecastData || isForecastLoading) && (
                <>
                  <div className="h-5 w-px bg-border" />
                  <Toggle
                    size="sm"
                    variant="outline"
                    pressed={showForecast && !isForecastLoading}
                    onPressedChange={() => setShowForecast((v) => !v)}
                    disabled={isForecastLoading}
                    className="gap-1.5 text-xs"
                  >
                    {isForecastLoading ? (
                      <Loader2Icon className="w-2.5 h-2.5 shrink-0 animate-spin" />
                    ) : (
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{
                          background:
                            "linear-gradient(90deg, #E2CBFF, #393BB2)",
                        }}
                      />
                    )}
                    {t("company.financials.forecast.toggle")}
                  </Toggle>
                </>
              )}
            </div>

            <ChartContainer config={chartConfig} className="h-[350px] w-full">
              {chartType === "bar" ? (
                <BarChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                >
                  {hasForecast && forecastGradientDef}
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis {...sharedAxisProps.xAxis} />
                  <YAxis {...sharedAxisProps.yAxis} />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent formatter={tooltipFormatter} />
                    }
                  />
                  {activeMetrics.map((metric) => (
                    <Fragment key={metric}>
                      <Bar
                        dataKey={metric}
                        fill={`var(--color-${metric})`}
                        radius={[4, 4, 0, 0]}
                        isAnimationActive={false}
                      />
                      {hasForecast &&
                        forecastData[metric] != null && (
                          <Bar
                            dataKey={FORECAST_KEY_MAP[metric]}
                            fill="url(#forecastGradient)"
                            fillOpacity={0.5}
                            radius={[4, 4, 0, 0]}
                            isAnimationActive={false}
                          />
                        )}
                    </Fragment>
                  ))}
                </BarChart>
              ) : (
                <LineChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                >
                  {hasForecast && forecastGradientDef}
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis {...sharedAxisProps.xAxis} />
                  <YAxis {...sharedAxisProps.yAxis} />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent formatter={tooltipFormatter} />
                    }
                  />
                  {activeMetrics.map((metric) => (
                    <Fragment key={metric}>
                      <Line
                        type="monotone"
                        dataKey={metric}
                        stroke={`var(--color-${metric})`}
                        strokeWidth={2}
                        dot={{ r: 4, fill: `var(--color-${metric})` }}
                        activeDot={{ r: 6 }}
                        isAnimationActive={false}
                      />
                      {hasForecast &&
                        forecastData[metric] != null && (
                          <Line
                            type="monotone"
                            dataKey={FORECAST_KEY_MAP[metric]}
                            stroke="url(#forecastGradient)"
                            strokeWidth={2}
                            strokeDasharray="6 4"
                            dot={false}
                            activeDot={false}
                            connectNulls={false}
                            isAnimationActive={false}
                          />
                        )}
                    </Fragment>
                  ))}
                </LineChart>
              )}
            </ChartContainer>
          </TabsContent>
        </Tabs>

      </CardContent>
    </Card>
  );
}
